/*
<purpose>
  The ARBEITSPLATZ shell for the ?popout=<uuid> window (operator ruling Q1 = E, Q4 = B,
  2026-09-17): the whole ticket, stacked in reading order at the WINDOW's full width, for a clerk
  who keeps it beside Outlook / S/4 while they work it. Same SPA, same providers, same
  TicketDetailComponent as the list's detail pane. The window IS the ticket (no NavBar, no
  footer) — and since Q4 B, no bar of its own either: the window TITLE carries the subject, the
  detail's own header is the one header.
</purpose>
<dependencies>
  smarttix.getTicket / getPiiIgnoreList / addPiiIgnore / downloadDraft; TicketManagerContext
  mutators (announce cross-window); ticketSync.onTicketMutated (refetch on foreign mutations);
  useTicketDetailRefresh (transient statuses); ReanalysisModal.
</dependencies>
<constraints>
  - Parallel windows: several popouts may exist; each self-syncs. Re-opening the same uuid
    focuses the existing window via the per-uuid window NAME in openTicketPopout.
  - PII posture unchanged: no reveal context here (Antworten/Archiv posture, server-enforced).
  - Status-driven gating in TicketDetailComponent stays the single action authority.
  - NO chrome of its own. A header here restated the subject the window title already carries and
    reserved a ZoneChip triage rows never fill; a centred max-width column left the width the
    clerk gave the window unused. Anything added here has to earn a place the list's detail pane
    does not already give it.
  - The maintenance banner is App's in the main shell and MUST be rendered here: this window is a
    separate React root outside that layout, and a window that keeps working while writes are
    paused has to say why they are refused.
  - The stage inside this window gets the PLAIN inset (COMPARE_STAGE_INSET): there is no sticky
    NavBar above it to clear.
</constraints>
*/
import React, { useCallback, useEffect, useRef, useState } from 'react';
import TicketDetailComponent from './TicketDetail';
import ReanalysisModal from './ReanalysisModal';
import { MaintenanceBanner } from './MaintenanceBanner';
import { COMPARE_STAGE_INSET } from './CompareStage';
import { theme } from '../theme';
import { smarttix } from '../services/api';
import { UserRoles, TicketDetail, ReanalysisRequest, PiiIgnoreEntry, DraftKind } from '../serverTypes';
import { formatBackendError } from '../utils/formatError';
import { COPY_TOAST_MS } from '../timings';
import { copyToastMessage } from '../words';
import { useTicketManager } from '../context/TicketManagerContext';
import { useMaintenanceMode } from '../context/MaintenanceContext';
import { RueckgaengigToast } from './RueckgaengigToast';
import { isSettledStatus } from '../services/settledRow';
import { usePress } from '../hooks/usePress';
import { pressHeadingVerdict } from '../services/pressVerdict';
import { declineInsteadWord } from '../services/declineVerdict';
import type { AfterPressView } from './PressBar';
import { STALE_SUGGESTION_TOAST, isStaleSuggestionError } from '../services/staleSuggestion';
import { isTriageDecidable } from '../services/draftClaims';
import { useTicketDetailRefresh } from '../hooks/useTicketDetailRefresh';
import { onTicketMutated } from '../services/ticketSync';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';

export function openTicketPopout(uuid: string): void {
  const url = `${window.location.pathname}?popout=${encodeURIComponent(uuid)}`;
  window.open(url, `stx-popout-${uuid}`, 'width=920,height=1040');
}

interface PopoutShellProps {
  uuid: string;
  userRoles: UserRoles | null;
  simulateSystemData: boolean;
  // ⚠ The pop-out is a SEPARATE window mounting the same TicketDetail. It takes the flag as a prop
  // from the same App-level /config read the main window uses — a second independent read here is
  // what would let one window show the row while the other hides it for the same ticket.
  showSourceChannel?: boolean;
  // Tenant-wide inline-image counting rule, threaded from the same App-level /config read as
  // showSourceChannel above — this window is a SEPARATE React root and would silently desync from
  // the main window if it read config on its own.
  attachmentsCountInline?: boolean;
}

const PopoutShell: React.FC<PopoutShellProps> = ({ uuid, userRoles, simulateSystemData, showSourceChannel, attachmentsCountInline }) => {
  const tm = useTicketManager();
  const cm = useCategoryMeta();
  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showReanalysisModal, setShowReanalysisModal] = useState(false);
  const [piiIgnoreList, setPiiIgnoreList] = useState<PiiIgnoreEntry[]>([]);
  // Copy feedback, both shapes (Part C, 2026-09-07). `kind` absent = the letter flow's
  // whole-suggestion copy, present = that one triage draft — the same discriminator AntwortenSeite
  // uses, because the two windows must offer the identical Rückgängig window for the same act.
  const [copyToast, setCopyToast] = useState<{ uuid: string; kind?: DraftKind } | null>(null);
  // Outcomes that are not errors (Part C, 2026-09-07): a reloaded stale row, a composer
  // refusal. They used to land in this window's ERROR banner for want of anywhere else — the main
  // page has had an info toast for them all along, and the two windows owe the same account of the
  // same event.
  const [infoToast, setInfoToast] = useState<string | null>(null);
  const maintenance = useMaintenanceMode();
  // The window's own focus destination, the same landmark the list's detail pane uses
  // (AntwortenSeite): a freshly opened pop-out had focus on nothing at all, so a keyboard clerk
  // started tabbing from the document root. Focused once, when the ticket first lands.
  const detailRegionRef = useRef<HTMLDivElement>(null);
  const focused = useRef(false);

  const refetch = useCallback(async () => {
    const detail = await smarttix.getTicket(uuid);
    setTicket(detail);
    setLoading(false);
  }, [uuid]);

  useEffect(() => { refetch(); }, [refetch]);
  useEffect(() => { smarttix.getPiiIgnoreList().then(setPiiIgnoreList); }, []);
  useTicketDetailRefresh(ticket, setTicket);

  // Foreign-window mutations of THIS ticket (or bulk '*') → refetch. Own actions announce
  // via tm but BroadcastChannel never echoes to the posting window — no self-loops.
  useEffect(() => onTicketMutated(u => { if (u === uuid || u === '*') refetch(); }), [uuid, refetch]);

  useEffect(() => {
    if (ticket?.subject) document.title = `${ticket.subject} — Smarttix`;
  }, [ticket?.subject]);

  useEffect(() => {
    if (loading || focused.current) return;
    focused.current = true;
    detailRegionRef.current?.focus();
  }, [loading]);

  // This window holds no lists, so a stale row's whole remedy is the refetch every success already
  // runs, plus the sentence saying it happened. Wired at THIS chokepoint rather than per handler:
  // every mutation here passes through runAction, so none of them can forget it.
  const handleStale = useCallback(async () => {
    await refetch();
    setInfoToast(STALE_SUGGESTION_TOAST);
  }, [refetch]);

  const runAction = async (fn: () => Promise<{ success: boolean; error?: string }>, fallbackMsg: string) => {
    setError(null);
    setActionLoading(true);
    const result = await fn();
    setActionLoading(false);
    if (result.success) await refetch();
    else if (isStaleSuggestionError(result)) await handleStale();
    else setError(result.error || fallbackMsg);
    // Returned so a caller can add its own post-effect (the copy toasts below) WITHOUT duplicating
    // the banner/spinner/refetch handling every mutation in this window goes through.
    return result;
  };

  // Copying here has always recorded the credit and said nothing about it — this window was the one
  // place a copy-approval could not be taken back. Both shapes now raise the same toast the main
  // page raises. Copying NEVER moves the ticket, in either shape.
  const handleCopyApprove = async () => {
    const result = await runAction(() => tm.copyApproveTicket(uuid), 'Fehler beim Kopieren');
    if (result.success) setCopyToast({ uuid });
  };

  // The press (decide, then step; decision 2026-10-28): the same flow as the list page — optimistic
  // verdict, the after-press card over the pressed slot, Rückgängig 30 s — without a list to step
  // through, so the card offers no Nächste Mail.
  const press = usePress({
    patchTicket: (u, fn) => setTicket(prev => (prev && prev.uuid === u ? fn(prev) : prev)),
    refetchTicket: async () => { await refetch(); },
    onStale: () => { void handleStale(); },
    onPressed: () => setCopyToast(null),
  });
  const afterPress: AfterPressView | null = press.press ? {
    uuid: press.press.uuid,
    mode: press.press.mode,
    verdictText: pressHeadingVerdict({
      verdict: press.press.verdict, lastKind: press.press.lastKind,
      declineWord: declineInsteadWord(cm, press.press.decline), statesKind: cm.statesApprovedKind,
    }, cm.face),
    subject: press.press.subject,
    side: press.press.side,
    undoLive: press.undoLive,
    onUndo: () => { void press.undo(); },
    onStay: press.toLine,
  } : null;

  // Per-draft copy. The clipboard write is unconditional; the CLAIM is owed only while the row is
  // still the clerk's to settle — a settled row, maintenance, or a kind that already carries a
  // claim are clipboard-only (the server answers stale_rejected for a second claim on one kind, so
  // asking would turn a successful copy into a red banner).
  // Returns whether the CLIPBOARD write happened — the card confirms itself on that alone, the
  // same contract the main page's handler answers. The credit that follows is reported in this
  // window's own banner and never revokes the confirmation of a copy that demonstrably occurred.
  const handleCopyDraft = async (kind: DraftKind, text: string): Promise<boolean> => {
    // The card awaits this only for that boolean: a rejected write must be reported here, not escape.
    try { await navigator.clipboard.writeText(text); }
    catch { setError('Fehler beim Kopieren'); return false; }
    const suggestion = ticket?.suggestion;
    // Same rule as the main page, from the same declared set (walk fix round 6, 2026-09-09) —
    // the two handlers are separate because each owns its own toast and row bookkeeping, so the
    // STATUS question is the one thing that must not be re-decided per host.
    if (!suggestion || !isTriageDecidable(ticket?.status) || maintenance) return true;
    if (suggestion.claims?.[kind]) return true;
    const result = await runAction(() => tm.claimDraft(uuid, suggestion.id, kind, 'copy'), 'Fehler beim Kopieren');
    if (result.success) setCopyToast({ uuid, kind });
    return true;
  };

  // No row marker to revert here: this window holds no lists, so the release's own refetch is what
  // restores the claim chips. That is deliberately NOT the main page's rule — there the badge lives
  // on a list row and must survive the release while another copy claim still stands.
  const handleUndoCopy = async () => {
    const toast = copyToast;
    setCopyToast(null);
    if (!toast) return;
    await runAction(
      () => (toast.kind ? tm.unclaimDraft(toast.uuid, toast.kind) : tm.undoCopyApprove(toast.uuid)),
      'Fehler beim Zurücknehmen');
  };

  return (
    <div style={{ fontFamily: theme.font.family, minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: theme.colors.bgPage }}>
      {/* Above the ticket, exactly where the main shell puts it. */}
      <MaintenanceBanner />

      {/* Page padding only — the window's width IS the working width (Q1 E). */}
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto',
        padding: theme.spacing.lg, width: '100%', boxSizing: 'border-box',
      }}>
        <div
          ref={detailRegionRef}
          role="region"
          aria-label="Ticketdetails"
          tabIndex={-1}
          style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', outline: 'none' }}
        >
        {!ticket && !loading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.colors.textSecondary, fontSize: 15 }}>
            Ticket nicht gefunden
          </div>
        ) : (
          <TicketDetailComponent
            // No NavBar over this window — the stage opens against the plain gap on all four sides.
            stageTopInset={COMPARE_STAGE_INSET}
            // The pop-out IS compare mode in its own window: it opens on the stage; Zurück shows the pane.
            initialCompare
            onInfo={setInfoToast}
            ticket={ticket as TicketDetail}
            loading={loading}
            actionLoading={actionLoading}
            error={error}
            onClearError={() => setError(null)}
            onApprove={() => { if (ticket) void press.approve(ticket); }}
            onDecline={async instead => { if (ticket) await press.decline(ticket, instead); }}
            onApproveKind={kind => { if (ticket) void press.approveKind(ticket, kind); }}
            afterPress={afterPress}
            pressMessage={press.message}
            onDismissPressMessage={press.dismissMessage}
            pressKeys={!showReanalysisModal}
            onCopyApprove={handleCopyApprove}
            onCopyDraft={handleCopyDraft}
            onReject={() => setShowReanalysisModal(true)}
            onDownload={async () => {
              try { await smarttix.downloadDraft(uuid); }
              catch (err: any) { setError(formatBackendError(err, 'Herunterladen des Entwurfs')); }
            }}
            onReprocess={ticket?.status === 'pipeline_error'
              ? () => runAction(() => tm.processTicket(uuid), 'Fehler beim erneuten Verarbeiten')
              : undefined}
            onArchive={ticket && (isSettledStatus(ticket.status) || ticket.status === 'pipeline_error')
              ? () => runAction(() => tm.archiveTicket(uuid), 'Fehler beim Archivieren')
              : undefined}
            onRestore={ticket?.status === 'archived'
              ? () => runAction(() => tm.unarchiveTicket(uuid), 'Fehler beim Wiederherstellen')
              : undefined}
            piiIgnoreList={piiIgnoreList}
            onPiiIgnore={async (fieldType, value) => {
              await smarttix.addPiiIgnore(fieldType, value);
              setPiiIgnoreList(prev => [...prev, { id: 0, fieldType, value }]);
            }}
            onSuggestionEdited={refetch}
            // onTriageClaimed is deliberately NOT wired (2026-09-07): it is the LIST post-effect,
            // and this window holds no lists. TicketDetail fires onSuggestionEdited on the very
            // same success, so a handler here would only be a second read of the same ticket —
            // which is exactly what the main page's own handler documents itself as avoiding.
            onActionError={setError}
            onStaleSuggestion={handleStale}
            simulateSystemData={simulateSystemData}
            showSourceChannel={showSourceChannel}
            attachmentsCountInline={attachmentsCountInline}
          />
        )}
        </div>
      </div>

      <RueckgaengigToast
        visible={!!infoToast}
        message={infoToast ?? ''}
        onDismiss={() => setInfoToast(null)}
      />

      <RueckgaengigToast
        visible={!!copyToast}
        showKey={copyToast?.uuid}
        message={copyToastMessage(copyToast?.kind, cm.face)}
        durationMs={COPY_TOAST_MS}
        onUndo={handleUndoCopy}
        onDismiss={() => setCopyToast(null)}
      />

      {showReanalysisModal && ticket && (
        <ReanalysisModal
          ticketUuid={ticket.uuid}
          currentCategory={ticket.primaryAxis}
          reanalysisCount={ticket.reanalysisCount}
          reanalysisBlocked={ticket.reanalysisBlocked}
          settled={isSettledStatus(ticket.status)}
          attachments={ticket.attachments}
          ocrDecisions={ticket.ocrDecisions}
          onSubmit={async (request: ReanalysisRequest) => {
            await runAction(() => tm.requestReanalysis(uuid, request), 'Fehler beim Ablehnen');
            setShowReanalysisModal(false);
          }}
          onUnblock={() => runAction(() => tm.unblockReanalysis(uuid), 'Fehler beim Entsperren')}
          onClose={() => setShowReanalysisModal(false)}
          isSubmitting={actionLoading}
          isAdmin={!!(userRoles?.isCustomerAdmin || userRoles?.isAdmin)}
          // Same value TicketDetailComponent above receives, from the same App-level /config read:
          // the modal opens ON TOP of that detail pane, so a dropped prop here puts two different
          // attachment counts on screen at the same time for the same ticket.
          attachmentsCountInline={attachmentsCountInline}
        />
      )}
    </div>
  );
};

export default PopoutShell;
